import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';
import { initializePushNotifications } from '../services/notificationService';
import { setSentryUser } from '../lib/sentry';

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  profileImage?: string | null;
  billingAccess?: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string, twoFactorToken?: string) => Promise<any>;
  register: (email: string, password: string, name: string) => Promise<void>;
  logout: () => void;
  refreshProfile: () => Promise<void>;
  setAuthData: (token: string, user: User, refreshToken?: string) => void;
  isAuthenticated: boolean;
  isLoading: boolean;
  isSuperAdmin: boolean;
  /** נציג טלפוני — the dialer and a read-only customer card, nothing else. */
  isAgent: boolean;
  hasBillingAccess: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const logout = useCallback(() => {
    // AUDIT-026: notify server so the refresh token is revoked in the DB.
    // Fire-and-forget; we purge local state regardless of server response.
    const storedRefresh = localStorage.getItem('refreshToken');
    if (storedRefresh) {
      axios.post(`${API_URL}/api/auth/logout`, { refreshToken: storedRefresh }).catch(() => {});
    }
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    setToken(null);
    setUser(null);
    setIsInitialized(false);
    delete axios.defaults.headers.common['Authorization'];
    setSentryUser(null);
  }, []);

  const fetchUserProfile = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users/profile`);
      setUser(response.data);
      setSentryUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      // If token is invalid, logout
      logout();
    }
  }, [logout]);

  useEffect(() => {
    // Set up axios interceptor for automatic token inclusion
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        const currentToken = localStorage.getItem('token');
        if (currentToken) {
          config.headers.Authorization = `Bearer ${currentToken}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // AUDIT-068: refresh-once-then-logout pattern. On 401 we try the
    // refresh endpoint once, store the new access+refresh pair, retry
    // the original request exactly once, and only log the user out if
    // the refresh itself fails. Each original request is tagged with
    // `_retry` so we never loop on repeated 401s.
    //
    // CROSS-TAB: refresh tokens ROTATE — a successful refresh revokes the
    // presented token. `refreshInFlight` only de-dupes within one tab, so two
    // CRM contexts (a second tab, or the pop-out phone window, which is a real
    // window) would both refresh with the same stored token: the first wins,
    // the second gets 401 on an already-revoked token and used to log the user
    // straight out. That is why sessions dropped seemingly at random — it
    // needed both windows to refresh in the same moment.
    //
    // Fix: take a short-lived lock in localStorage (visible to every tab) so
    // only one context calls /refresh; the others poll for the token it
    // stores. And before ever logging out on a failed refresh, re-read
    // localStorage: another tab may have just rotated it for us.
    const REFRESH_LOCK_KEY = 'auth.refreshLock';
    const LOCK_TTL_MS = 10_000;      // a refresh that takes longer than this is dead
    const WAIT_STEP_MS = 150;
    const WAIT_MAX_MS = 12_000;

    let refreshInFlight: Promise<string | null> | null = null;

    /** Try to become the tab that performs the refresh. */
    function acquireLock(): boolean {
      try {
        const now = Date.now();
        const raw = localStorage.getItem(REFRESH_LOCK_KEY);
        if (raw && now - Number(raw) < LOCK_TTL_MS) return false; // someone else is on it
        localStorage.setItem(REFRESH_LOCK_KEY, String(now));
        return true;
      } catch {
        return true; // storage unavailable — behave as before
      }
    }
    const releaseLock = () => { try { localStorage.removeItem(REFRESH_LOCK_KEY); } catch { /* noop */ } };

    /** Wait for whichever tab holds the lock to publish a new access token. */
    async function waitForOtherTab(previousAccess: string | null): Promise<string | null> {
      const deadline = Date.now() + WAIT_MAX_MS;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, WAIT_STEP_MS));
        const current = localStorage.getItem('token');
        if (current && current !== previousAccess) return current; // they rotated it
        const lock = localStorage.getItem(REFRESH_LOCK_KEY);
        if (!lock || Date.now() - Number(lock) >= LOCK_TTL_MS) return null; // holder died
      }
      return null;
    }

    async function tryRefresh(): Promise<string | null> {
      if (refreshInFlight) return refreshInFlight;
      refreshInFlight = (async () => {
        const before = localStorage.getItem('token');
        const storedRefresh = localStorage.getItem('refreshToken');
        if (!storedRefresh) return null;

        if (!acquireLock()) {
          // Another tab is refreshing. Use its result rather than burning our
          // (now soon-to-be-revoked) copy of the refresh token.
          const fromOther = await waitForOtherTab(before);
          if (fromOther) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${fromOther}`;
            return fromOther;
          }
          // Holder died without publishing — fall through and do it ourselves.
        }

        try {
          const resp = await axios.post(`${API_URL}/api/auth/refresh`, {
            // Re-read: another tab may have rotated it while we waited.
            refreshToken: localStorage.getItem('refreshToken') || storedRefresh,
          });
          const newAccess = resp.data?.token as string | undefined;
          const newRefresh = resp.data?.refreshToken as string | undefined;
          if (!newAccess || !newRefresh) return null;
          localStorage.setItem('token', newAccess);
          localStorage.setItem('refreshToken', newRefresh);
          axios.defaults.headers.common['Authorization'] = `Bearer ${newAccess}`;
          return newAccess;
        } catch {
          // Lost a race we did not detect: if the stored access token changed
          // under us, another tab succeeded and this session is still valid.
          const current = localStorage.getItem('token');
          if (current && current !== before) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${current}`;
            return current;
          }
          return null;
        } finally {
          releaseLock();
          refreshInFlight = null;
        }
      })();
      return refreshInFlight;
    }

    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const original = error.config;
        const isRefreshCall = typeof original?.url === 'string' && original.url.includes('/api/auth/refresh');
        if (
          error.response?.status === 401 &&
          original &&
          !original._retry &&
          !isRefreshCall
        ) {
          original._retry = true;
          const newAccess = await tryRefresh();
          if (newAccess) {
            original.headers = original.headers || {};
            original.headers.Authorization = `Bearer ${newAccess}`;
            return axios(original);
          }
          // Refresh failed — hard logout.
          logout();
        } else if (error.response?.status === 401 && isRefreshCall) {
          // Refresh itself returned 401. Before logging out, check whether
          // another tab rotated the token in the meantime — a revoked-token
          // 401 caused by a cross-tab race must not end the session.
          const current = localStorage.getItem('token');
          if (current && current !== original?.headers?.Authorization?.replace(/^Bearer /, '')) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${current}`;
          } else {
            logout();
          }
        }
        return Promise.reject(error);
      }
    );

    const initializeAuth = async () => {
      if (token && !isInitialized) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        await fetchUserProfile();
        setIsInitialized(true);
        setIsLoading(false);

        // Initialize push notifications if user is already logged in
        initializePushNotifications(token).catch(error => {
          console.error('Failed to initialize push notifications:', error);
        });
      } else if (!token) {
        setIsInitialized(true);
        setIsLoading(false);
      }
    };

    initializeAuth();

    // Cleanup interceptors
    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [token, isInitialized]);

  const login = useCallback(async (email: string, password: string, twoFactorToken?: string) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        email,
        password,
        twoFactorToken
      });

      // Check if 2FA is required
      if (response.data.requires2FA) {
        return { requires2FA: true };
      }

      // Check if 2FA setup is required — pass the setup-scoped token through so
      // the login page can enroll the user in 2FA without a full session.
      if (response.data.requires2FASetup) {
        return {
          requires2FASetup: true,
          message: response.data.message,
          setupToken: response.data.setupToken,
        };
      }

      const { token, refreshToken, user } = response.data;
      localStorage.setItem('token', token);
      // AUDIT-026/068: persist refresh token for rotation.
      if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
      setToken(token);
      setUser(user);
      setSentryUser(user);
      setIsInitialized(true);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // Initialize push notifications after successful login
      initializePushNotifications(token).catch(error => {
        console.error('Failed to initialize push notifications:', error);
      });

      return { success: true };
    } catch (error: any) {
      console.error('Login error:', error);
      console.error('Error response:', error.response);
      throw new Error(error.response?.data?.error || 'Invalid credentials');
    }
  }, []);

  const register = useCallback(async (email: string, password: string, name: string) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/register`, {
        email,
        password,
        name
      });

      const { token, refreshToken, user } = response.data;
      localStorage.setItem('token', token);
      if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
      setToken(token);
      setUser(user);
      setSentryUser(user);
      setIsInitialized(true);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } catch (error) {
      throw new Error('Registration failed');
    }
  }, []);

  const setAuthData = useCallback((newToken: string, newUser: User, newRefreshToken?: string) => {
    localStorage.setItem('token', newToken);
    if (newRefreshToken) localStorage.setItem('refreshToken', newRefreshToken);
    setToken(newToken);
    setUser(newUser);
    setSentryUser(newUser);
    setIsInitialized(true);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;

    // Initialize push notifications
    initializePushNotifications(newToken).catch(error => {
      console.error('Failed to initialize push notifications:', error);
    });
  }, []);

  const value = useMemo<AuthContextType>(() => ({
    user,
    token,
    login,
    register,
    logout,
    refreshProfile: fetchUserProfile,
    setAuthData,
    isAuthenticated: !!token,
    isLoading,
    isSuperAdmin: user?.role === 'super_admin',
    isAgent: user?.role === 'agent',
    hasBillingAccess: user?.role === 'super_admin' || user?.billingAccess === true,
  }), [user, token, isLoading, login, register, logout, fetchUserProfile, setAuthData]);

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};